import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useDispatch } from '_common/hooks';
import { navigateToSettings } from 'router/navigation';
import { useGetRolesListQuery } from '../RolesApi';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';

import { Table, Cell, type TableColumn, type TableRow } from '_common/components/Table';

import styles from './RolesTable.module.scss';

const RolesTable = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { data, isLoading } = useGetRolesListQuery();

  const handleDeleteClick = (role: Role) => {
    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          title: 'settings.tenant.deleteRoleTitle',
          titleValues: { name: role.name },
          message: 'settings.tenant.deleteRoleMessage',
          messageValues: { name: role.name },
          confirmButtonTextId: 'settings.tenant.deleteRoleConfirm',
          confirmButtonType: 'danger',
          cancelButtonTextId: 'global.cancel',
          actionCode: 'deletedRole',
          actionValue: { roleId: role.id },
          cancelButtonShow: true,
        },
      }),
    );
  };

  const columns = useMemo<TableColumn[]>(() => {
    if (!data) return [];
    return [
      {
        id: 'name',
        accessorKey: 'name',
        size: 208,
        header: intl.formatMessage({ id: 'settings.tenant.roleName' }),
        cell: (info) => {
          const role = data.dict[info.row.id];
          return <Cell testId={`${role.id}-name-column`}>{role.name}</Cell>;
        },
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: intl.formatMessage({ id: 'global.description' }),
        cell: (info) => {
          const role = data.dict[info.row.id];
          return (
            <Cell testId={`${role.id}-description-column`}>
              {role.description ? (
                role.description
              ) : (
                <Cell.Empty>{intl.formatMessage({ id: 'NO_DESCRIPTION' })}</Cell.Empty>
              )}
            </Cell>
          );
        },
      },
      {
        id: 'creationDate',
        accessorKey: 'time.creation',
        size: 160,
        sortingFn: 'datetime',
        header: intl.formatMessage({ id: 'global.creationDate' }),
        cell: (info) => {
          const role = data.dict[info.row.id];
          return (
            <Cell testId={`${role.id}-creationDate-column`}>
              <Cell.DateTime
                date={{ date: role.time.creation }}
                time={{ time: role.time.creation }}
              />
            </Cell>
          );
        },
      },
    ];
  }, [data]);

  const rowsData = useMemo<TableRow[]>(() => {
    return (
      data?.list.map<TableRow>((roleId) => {
        const role = data.dict[roleId];
        return {
          ...role,
          suffix: {
            actions: [
              {
                label: intl.formatMessage({ id: 'global.settings' }),
                onClick: () => navigateToSettings('role', role.id),
              },
              {
                label: intl.formatMessage({ id: 'global.delete' }),
                onClick: () => handleDeleteClick(role),
              },
            ],
          },
        };
      }) ?? []
    );
  }, [data]);

  return (
    <div className={styles.root}>
      <Table
        identity="roles"
        options={{ columns, data: rowsData }}
        renderFooter={() => null}
        isLoading={isLoading}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        testId="roles"
        selectable={false}
      />
    </div>
  );
};

export default RolesTable;
